// src/components/tokenPage/risk/BetaCorrelationAndCaptureCard.tsx

import React, { useMemo, useState } from "react";
import { MetricBox } from "@/components/tokenPage/shared/TokenDashboardComponents";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import TimeSeriesChart from "@/components/charts/base/TimeSeriesChart";
import { DataCard } from "@/components/ui";
import { useToken } from "@/contexts";
import { useTokenPrice } from "@/contexts/token";
import {
  computeRollingBetaCorrelation,
  upsideCapture,
  downsideCapture,
  captureSpread,
  stdDev,
} from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';

const RANGE_TO_DAYS: Record<Range, number> = { '90d': 90, '180d': 180, '365d': 365, 'All': Infinity };

const BetaCorrelationAndCaptureCard: React.FC = () => {
  const { ticker } = useToken();
  const [bench, setBench] = useState<'BTC'|'ETH'>('BTC');
  const [range, setRange] = useState<Range>('365d');
  const [win, setWin] = useState<30|90|180|365>(90);
  
  const { aligned, isLoading } = useTokenPrice();
  
  // Destructure aligned data
  const alignedReturns = aligned.tokenReturns;
  const alignedBtcReturns = aligned.btcReturns;
  const alignedEthReturns = aligned.ethReturns;
  const alignedDates = aligned.dates;

  const { dates, corr, beta, upCap, downCap, spread, stability } = useMemo(() => {
    if (alignedReturns.length === 0 || alignedDates.length === 0) {
      return { dates: [], corr: [], beta: [], upCap: null, downCap: null, spread: null, stability: null };
    }

    const benchReturns = bench === 'BTC' ? alignedBtcReturns : alignedEthReturns;
    
    // Apply range filter
    const maxPoints = RANGE_TO_DAYS[range];
    const start = maxPoints === Infinity ? 0 : Math.max(0, alignedDates.length - maxPoints);
    const datesSliced = alignedDates.slice(start);
    const returnsSliced = alignedReturns.slice(start);
    const benchSliced = benchReturns.slice(start);
    
    // Compute rolling beta and correlation (output is same length as input)
    const minPts = win === 30 ? 20 : win === 90 ? 60 : win === 180 ? 120 : 180;
    const { rollingBeta, rollingCorrelation } = computeRollingBetaCorrelation(
      returnsSliced,
      benchSliced,
      win,
      minPts
    );
    
    // Find first valid index to trim warmup period
    const corrFirst = rollingCorrelation.findIndex(v => v !== null && Number.isFinite(v));
    const betaFirst = rollingBeta.findIndex(v => v !== null && Number.isFinite(v));
    const validIndices = [corrFirst, betaFirst].filter(idx => idx >= 0);
    const firstValidIdx = validIndices.length > 0 ? Math.min(...validIndices) : -1;
    
    const datesFinal = firstValidIdx >= 0 ? datesSliced.slice(firstValidIdx) : datesSliced;
    const corrFinal = firstValidIdx >= 0 ? rollingCorrelation.slice(firstValidIdx) : rollingCorrelation;
    const betaFinal = firstValidIdx >= 0 ? rollingBeta.slice(firstValidIdx) : rollingBeta;
    
    // Capture metrics over last 90d
    const last90Returns = alignedReturns.slice(-90);
    const last90Bench = benchReturns.slice(-90);
    
    const upCapVal = upsideCapture(last90Returns, last90Bench);
    const downCapVal = downsideCapture(last90Returns, last90Bench);
    const spreadVal = captureSpread(last90Returns, last90Bench);
    
    // Calculate capture stability (std dev of daily capture ratios)
    const perDayCapture: number[] = [];
    for (let i = 0; i < Math.min(last90Returns.length, last90Bench.length); i++) {
      const br = last90Bench[i];
      const tr = last90Returns[i];
      if (br !== undefined && tr !== undefined && Number.isFinite(br) && br > 0 && Number.isFinite(tr)) {
        perDayCapture.push(tr / br);
      }
    }
    const stabilityVal = stdDev(perDayCapture);
    
    return { 
      dates: datesFinal, 
      corr: corrFinal, 
      beta: betaFinal, 
      upCap: upCapVal, 
      downCap: downCapVal, 
      spread: spreadVal, 
      stability: stabilityVal 
    };
  }, [alignedReturns, alignedBtcReturns, alignedEthReturns, alignedDates, bench, range, win]);

  const benchOptions: Array<'BTC'|'ETH'> = ticker.toUpperCase() === 'BTC' 
    ? ['ETH'] 
    : (ticker.toUpperCase() === 'ETH' ? ['BTC'] : ['BTC','ETH']);

  // Hide if no data available (after loading)
  const hasData = corr.some(v => v !== null && Number.isFinite(v)) || 
                  beta.some(v => v !== null && Number.isFinite(v)) || 
                  upCap != null || downCap != null;
  if (!isLoading && !hasData) {
    return null;
  }

  // Convert null to undefined for chart compatibility
  const corrValues = corr.map(v => (v !== null && Number.isFinite(v)) ? v : null);
  const betaValues = beta.map(v => (v !== null && Number.isFinite(v)) ? v : null);

  return (
    <DataCard
      title="Beta, Correlation & Capture"
      subtitle="Rolling vs benchmark with capture stats"
      icon="share-2"
      iconColor="text-slate-300"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400">Window</span>
          {[30, 90, 180, 365].map(w => (
            <button 
              key={w}
              className={`text-xs px-2 py-1 rounded-md ${win===w?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} 
              onClick={() => setWin(w as 30|90|180|365)}
            >
              {w}d
            </button>
          ))}
          <span className="text-[11px] text-slate-500 mx-1">|</span>
          <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
          {benchOptions.map(b => (
            <button 
              key={b} 
              className={`text-xs px-2 py-1 rounded-md ${bench===b?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} 
              onClick={() => setBench(b)}
            >
              {b}
            </button>
          ))}
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="h-[280px] md:h-[320px]">
          <TimeSeriesChart
            dates={dates}
            series={[{
              name: `Corr ${win}d vs ${bench}`,
              values: corrValues,
              color: '#60a5fa',
              areaStyle: true,
            }]}
            height="100%"
            yAxisFormat="ratio"
            showLegend={true}
          />
        </div>
        <div className="h-[280px] md:h-[320px]">
          <TimeSeriesChart
            dates={dates}
            series={[{
              name: `Beta ${win}d vs ${bench}`,
              values: betaValues,
              color: '#34d399',
              areaStyle: true,
            }]}
            height="100%"
            yAxisFormat="ratio"
            showLegend={true}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3">
        <MetricBox 
          label={`Beta (${win}d vs ${bench})`} 
          value={beta.length ? (beta[beta.length-1] ?? null) : null} 
          fmt="ratio4" 
        />
        <MetricBox 
          label={`Correlation (${win}d vs ${bench})`} 
          value={corr.length ? (corr[corr.length-1] ?? null) : null} 
          fmt="ratio4" 
        />
        <MetricBox 
          label={`Upside Capture (90d vs ${bench})`} 
          value={upCap} 
          fmt="ratio2" 
          suffix="x" 
        />
        <MetricBox 
          label={`Downside Capture (90d vs ${bench})`} 
          value={downCap != null ? Math.abs(downCap) : null} 
          fmt="ratio2" 
          suffix="x" 
        />
        <MetricBox 
          label="Capture Spread (90d)" 
          value={spread} 
          fmt="ratio2" 
          suffix="x" 
          toneBySign 
        />
        <MetricBox 
          label="Capture Stability (90d)" 
          value={stability} 
          fmt="ratio2" 
        />
      </div>
    </DataCard>
  );
};

export default BetaCorrelationAndCaptureCard;
